import {
  formatDateTime,
  formatDayMonth,
  formatTime,
  localDateKey,
} from './format';
import type { OrganizationSyncStats, SyncOverview, SyncRun } from '@octave/contracts';

/**
 * Pure helpers for the admin sync monitoring view: date formatting,
 * per-organization problem detection, global health derivation and
 * client-side grouping of cron runs by night. No React, fully testable.
 */

export const STALE_LINES_THRESHOLD_HOURS = 48;

// --------------------------------------------------------------------------
// Formatting
// --------------------------------------------------------------------------

export const formatRelative = (iso: string | null, nowMs = Date.now()): string => {
  if (!iso) return 'Jamais';
  const diffMs = nowMs - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `il y a ${diffD}j`;
};

export const formatExact = (iso: string | null): string => {
  if (!iso) return '—';
  return formatDateTime(iso);
};

export { formatTime } from './format';


export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.floor((ms % 60_000) / 1000)} s`;
};

export const scopeLabel = (scope: string): string => {
  switch (scope) {
    case 'cron':
      return 'Cron';
    case 'manual':
      return 'Manuel';
    case 'initial':
      return 'Initial';
    default:
      return scope;
  }
};

/** "Nightly du 09/07, 02:00" */
export const formatNightLabel = (iso: string): string => {
  const date = new Date(iso);
  const day = formatDayMonth(date);
  return `Nightly du ${day}, ${formatTime(iso)}`;
};

const hoursSince = (iso: string | null, nowMs: number): number =>
  iso ? (nowMs - new Date(iso).getTime()) / 36e5 : Infinity;

// --------------------------------------------------------------------------
// Per-organization problem detection
// --------------------------------------------------------------------------

export type OrgProblemKind = 'details-failed' | 'lines-stale' | 'never-synced';

export interface OrgProblem {
  org: OrganizationSyncStats;
  kind: OrgProblemKind;
  /** Plain-French description of what is wrong. */
  message: string;
  /** The one relevant sync to (re)launch. */
  action: 'details' | 'lines';
}

/**
 * Rules:
 * - never-synced: no details sync AND no lines sync ever ran → suggest the
 *   details sync (first step of the nightly pipeline).
 * - details-failed: last details sync ended in failure.
 * - lines-stale: the org has lines but the last lines sync is older than 48h.
 * An org can appear twice (details failed + lines stale), each row carrying
 * its own action.
 */
export function detectOrgProblems(
  orgs: OrganizationSyncStats[],
  nowMs = Date.now()
): OrgProblem[] {
  const problems: OrgProblem[] = [];
  for (const org of orgs) {
    if (!org.lastDetailsSyncedAt && !org.lastLinesSyncedAt) {
      problems.push({
        org,
        kind: 'never-synced',
        message:
          'Jamais synchronisée : aucun sync (orgs & sites ou lignes) n’a encore tourné.',
        action: 'details',
      });
      continue;
    }
    if (org.lastDetailsStatus === 'failure') {
      problems.push({
        org,
        kind: 'details-failed',
        message: `Dernier sync orgs & sites en échec (${formatRelative(org.lastDetailsSyncedAt, nowMs)}).`,
        action: 'details',
      });
    }
    if (
      org.totalLines > 0 &&
      hoursSince(org.lastLinesSyncedAt, nowMs) > STALE_LINES_THRESHOLD_HOURS
    ) {
      problems.push({
        org,
        kind: 'lines-stale',
        message: `Lignes non synchronisées depuis plus de ${STALE_LINES_THRESHOLD_HOURS}h (dernier sync ${formatRelative(org.lastLinesSyncedAt, nowMs)}).`,
        action: 'lines',
      });
    }
  }
  return problems;
}

// --------------------------------------------------------------------------
// Global health derivation
// --------------------------------------------------------------------------

export type SyncHealthLevel = 'ok' | 'degraded' | 'broken';

export interface SyncHealth {
  level: SyncHealthLevel;
  /** Plain-French reasons, empty when everything is fine. */
  reasons: string[];
}

/**
 * Rules:
 * - broken (red): the very last run of either kind failed, or at least one
 *   org has its last details sync in failure.
 * - degraded (amber): stale lines exist globally, or at least one org has a
 *   non-blocking problem (lines stale, never synced).
 * - ok (green) otherwise.
 */
export function deriveSyncHealth(
  overview: SyncOverview,
  problems: OrgProblem[]
): SyncHealth {
  const brokenReasons: string[] = [];
  const degradedReasons: string[] = [];

  if (overview.lastRuns.organizationDetails?.status === 'failure') {
    brokenReasons.push('dernier sync orgs & sites en échec');
  }
  if (overview.lastRuns.lines?.status === 'failure') {
    brokenReasons.push('dernier sync lignes en échec');
  }
  const detailsFailedCount = problems.filter(
    (p) => p.kind === 'details-failed'
  ).length;
  if (detailsFailedCount > 0) {
    brokenReasons.push(
      `${detailsFailedCount} organisation(s) avec sync orgs & sites en échec`
    );
  }

  if (overview.lines.staleLines > 0) {
    degradedReasons.push(
      `${overview.lines.staleLines} ligne(s) non synchronisée(s) depuis plus de ${overview.lines.staleThresholdHours}h`
    );
  }
  const neverSyncedCount = problems.filter(
    (p) => p.kind === 'never-synced'
  ).length;
  if (neverSyncedCount > 0) {
    degradedReasons.push(
      `${neverSyncedCount} organisation(s) jamais synchronisée(s)`
    );
  }
  const staleOrgsCount = problems.filter((p) => p.kind === 'lines-stale').length;
  if (staleOrgsCount > 0) {
    degradedReasons.push(
      `${staleOrgsCount} organisation(s) avec lignes obsolètes`
    );
  }

  if (brokenReasons.length > 0) {
    return { level: 'broken', reasons: [...brokenReasons, ...degradedReasons] };
  }
  if (degradedReasons.length > 0) {
    return { level: 'degraded', reasons: degradedReasons };
  }
  return { level: 'ok', reasons: [] };
}

// --------------------------------------------------------------------------
// History grouping (cron runs bucketed by night)
// --------------------------------------------------------------------------

export interface NightGroup {
  type: 'night';
  /** Local calendar-day key (yyyy-mm-dd). */
  key: string;
  /** Earliest startedAt of the night (the cron kickoff, ~02:00). */
  startedAt: string;
  runs: SyncRun[];
  orgCount: number;
  linesUpserted: number;
  sitesUpserted: number;
  failures: number;
}

export interface SingleRunItem {
  type: 'run';
  run: SyncRun;
}

export type HistoryItem = NightGroup | SingleRunItem;

/** Local calendar-day key, e.g. "2026-07-09". */
const dayKey = (iso: string): string =>
  localDateKey(iso);

/**
 * Groups cron runs by local calendar day (the nightly cron fires at 02:00,
 * so one day = one night) into a single expandable summary item. Manual and
 * initial runs stay as individual items. Input is assumed sorted by
 * startedAt desc (backend contract); output preserves that order, a night
 * group sitting at the position of its most recent run.
 */
export function groupSyncHistory(runs: SyncRun[]): HistoryItem[] {
  const nightByKey = new Map<string, NightGroup & { orgIds: Set<string> }>();
  const items: HistoryItem[] = [];

  for (const run of runs) {
    if (run.scope !== 'cron') {
      items.push({ type: 'run', run });
      continue;
    }
    const key = dayKey(run.startedAt);
    let night = nightByKey.get(key);
    if (!night) {
      night = {
        type: 'night',
        key,
        startedAt: run.startedAt,
        runs: [],
        orgIds: new Set<string>(),
        orgCount: 0,
        linesUpserted: 0,
        sitesUpserted: 0,
        failures: 0,
      };
      nightByKey.set(key, night);
      items.push(night);
    }
    night.runs.push(run);
    // runs arrive sorted desc → the last one seen is the earliest of the night
    night.startedAt = run.startedAt;
    if (run.organizationId) night.orgIds.add(run.organizationId);
    if (run.kind === 'lines') night.linesUpserted += run.upserted;
    else night.sitesUpserted += run.upserted;
    if (run.status === 'failure') night.failures += 1;
    night.orgCount = night.orgIds.size;
  }

  return items;
}
